// src/components/Sidebar.js (UPDATED - Add Events Menu Item)
import React, { useState } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Avatar,
    Typography,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Dashboard,
    People,
    School,
    TrendingUp,
    Settings,
    Logout,
    ExpandLess,
    ExpandMore,
    Group,
    AutoStories,
    Build,
    Person,
    Event as EventIcon, // NEW
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { logoutUser } from "../firebase/auth";
import NimsLogo from "../assets/images/Nims_Logo.png";

export default function Sidebar({ open = false, onToggle }) {
    const navigate = useNavigate();
    const { userProfile, user } = useAuth();
    const [openMenu, setOpenMenu] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleLogout = async () => {
        try {
            await logoutUser();
            navigate("/login");
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    const handleMenuToggle = (menu) => {
        setOpenMenu((prev) => ({
            ...prev,
            [menu]: !prev[menu],
        }));
    };

    const menuItems = [
        {
            label: "Dashboard",
            icon: <Dashboard />,
            path:
                userProfile?.role === "admin"
                    ? "/admin"
                    : userProfile?.role === "startup"
                        ? "/startup"
                        : "/student",
            roles: ["admin", "student", "startup", "faculty"],
        },
        {
            label: "Profile",
            icon: <Person />,
            path:
                userProfile?.role === "admin"
                    ? "/admin"
                    : userProfile?.role === "startup"
                        ? "/startup/profile"
                        : "/student/profile",
            roles: ["admin", "student", "startup"],
        },
        {
            label: "Users",
            icon: <People />,
            path: "/admin/users",
            roles: ["admin"],
        },
        {
            label: "Teams",
            icon: <Group />,
            path: userProfile?.role === "admin" ? "/admin/teams" : "/student/teams",
            roles: ["admin", "student"],
        },
        // NEW: Events Menu Item
        {
            label: "Events",
            icon: <EventIcon />,
            submenu: [
                {
                    label: "Browse Events",
                    path: "/student/events",
                    roles: ["student"],
                },
                {
                    label: "My Events",
                    path: "/student/my-events",
                    roles: ["student"],
                },
                {
                    label: "Manage Events",
                    path: "/admin/events-v2",
                    roles: ["admin"],
                },
                {
                    label: "Event Quiz Builder",
                    path: "/admin/events-v2",
                    roles: ["admin"],
                },
                {
                    label: "Create Event",
                    path: "/admin/events-v2",
                    roles: ["admin"],
                },
            ],
            roles: ["student", "admin"],
        },
        {
            label: "Learning",
            icon: <School />,
            submenu: [
                {
                    label: "Quiz Manager",
                    path: "/admin/quiz",
                    roles: ["admin"],
                },
                {
                    label: "Browse Paths",
                    path: "/student/learning/paths",
                    roles: ["student"],
                },
                {
                    label: "My Progress",
                    path: "/student/learning/progress",
                    roles: ["student"],
                },
                {
                    label: "Manage Paths",
                    path: "/admin/learning-paths",
                    roles: ["admin"],
                },
                {
                    label: "Create Path",
                    path: "/admin/learning-paths/create",
                    roles: ["admin"],
                },
            ],
            roles: ["student", "admin"],
        },
        {
            label: "Rankings",
            icon: <TrendingUp />,
            path: "/student/ranking",
            roles: ["student", "startup"],
        },
        {
            label: "Credits",
            icon: <AutoStories />,
            submenu: [
                {
                    label: "My Credits",
                    path: "/student/credits/history",
                    roles: ["student"],
                },
                {
                    label: "Assign Credits",
                    path: "/admin/credits/assign",
                    roles: ["admin"],
                },
            ],
            roles: ["student", "admin"],
        },
        {
            label: "Startups",
            icon: <TrendingUp />,
            submenu: [
                {
                    label: "Browse Startups",
                    path: "/student/startups",
                    roles: ["student"],
                },
                {
                    label: "Create Startup",
                    path: "/student/startup/new",
                    roles: ["student"],
                },
                {
                    label: "My Startup",
                    path: "/student/my-startup",
                    roles: ["student"],
                },
                {
                    label: "All Startups",
                    path: "/admin/startups",
                    roles: ["admin"],
                },
            ],
            roles: ["admin", "startup"],
        },
        {
            label: "Form Builder",
            icon: <Build />,
            submenu: [
                { label: "Manage Forms", path: "/admin/forms", roles: ["admin"] },
                { label: "Create Evaluation", path: "/admin/forms/evaluation/new", roles: ["admin"] },
                { label: "Create Input Detail", path: "/admin/forms/input-detail/new", roles: ["admin"] },
                { label: "Create Feedback", path: "/admin/forms/feedback/new", roles: ["admin"] },
                { label: "Form Type Selector", path: "/admin/forms/create", roles: ["admin"] }
            ],
            roles: ["admin"],
        },
        {
            label: "Scoreboard",
            icon: <TrendingUp />,
            path: "/admin/scoreboard",
            roles: ["admin"],
        },
        {
            label: "Settings",
            icon: <Settings />,
            path: "/settings",
            roles: ["admin", "student", "startup", "faculty"],
        },
    ];

    const visibleItems = menuItems.filter((item) =>
        item.roles?.includes(userProfile?.role)
    );

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile && onToggle) onToggle(false);
    };

    const drawerOpen = isMobile ? !!open : true;

    const drawerContent = (
        <>
            {/* Logo Section */}
            <Box
                sx={{
                    height: 100,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #dae8f7 0%, #ffffff 100%)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -50,
                        right: -50,
                        width: 150,
                        height: 150,
                        background: "rgba(193, 39, 45, 0.1)",
                        borderRadius: "50%",
                    },
                }}
            >
                <Box
                    component="img"
                    src={NimsLogo}
                    alt="NIMS Logo"
                    sx={{
                        width: 180,
                        height: 180,
                        objectFit: "contain",
                        mb: 1,
                        filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.2))",
                    }}
                />

            </Box>

            {/* User Profile Section */}
            <Box
                sx={{
                    p: 2.5,
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #003366",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                        sx={{
                            width: 50,
                            height: 50,
                            bgcolor: "#C1272D",
                            fontWeight: 600,
                            fontSize: "1.2rem",
                            border: "2px solid #003366",
                        }}
                    >
                        {userProfile?.firstName?.charAt(0) || "U"}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: "#003366",
                                fontSize: "0.95rem",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {userProfile?.firstName} {userProfile?.lastName}
                        </Typography>
                        <Box
                            sx={{
                                display: "inline-block",
                                mt: 0.5,
                                px: 1,
                                py: 0.25,
                                bgcolor: "#C1272D",
                                borderRadius: "4px",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#fff",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                {userProfile?.role || "User"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Menu Items */}
            <List
                sx={{
                    py: 1,
                    px: 1,
                    flex: 1,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                        width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: "#003366",
                        borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        background: "#C1272D",
                    },
                }}
            >
                {visibleItems.map((item) => (
                    <React.Fragment key={item.label}>
                        {item.submenu ? (
                            <>
                                <ListItem
                                    button
                                    onClick={() => handleMenuToggle(item.label)}
                                    sx={{
                                        borderRadius: "8px",
                                        mb: 0.5,
                                        "&:hover": {
                                            backgroundColor: "rgba(193, 39, 45, 0.08)",
                                            transform: "translateX(4px)",
                                            transition: "all 0.2s ease",
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            color: "#003366",
                                            minWidth: 40,
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: 500,
                                            fontSize: "0.9rem",
                                            color: "#003366",
                                        }}
                                    />
                                    {openMenu[item.label] ? (
                                        <ExpandLess sx={{ color: "#003366" }} />
                                    ) : (
                                        <ExpandMore sx={{ color: "#003366" }} />
                                    )}
                                </ListItem>
                                <Collapse in={openMenu[item.label]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.submenu
                                            .filter((sub) => sub.roles?.includes(userProfile?.role))
                                            .map((sub) => (
                                                <ListItem
                                                    key={sub.label}
                                                    button
                                                    onClick={() => handleNavigate(sub.path)}
                                                    sx={{
                                                        pl: 6,
                                                        borderRadius: "8px",
                                                        mb: 0.5,
                                                        ml: 1,
                                                        "&:hover": {
                                                            backgroundColor: "rgba(0, 51, 102, 0.08)",
                                                            borderLeft: "3px solid #C1272D",
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={sub.label}
                                                        primaryTypographyProps={{
                                                            fontSize: "0.85rem",
                                                            color: "#555",
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                    </List>
                                </Collapse>
                            </>
                        ) : (
                            <ListItem
                                button
                                onClick={() => handleNavigate(item.path)}
                                sx={{
                                    borderRadius: "8px",
                                    mb: 0.5,
                                    "&:hover": {
                                        backgroundColor: "rgba(193, 39, 45, 0.08)",
                                        transform: "translateX(4px)",
                                        transition: "all 0.2s ease",
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        color: "#003366",
                                        minWidth: 40,
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: 500,
                                        fontSize: "0.9rem",
                                        color: "#003366",
                                    }}
                                />
                            </ListItem>
                        )}
                    </React.Fragment>
                ))}
            </List>

            {/* Logout Button */}
            <Box sx={{ p: 1, borderTop: "1px solid #e0e0e0" }}>
                <ListItem
                    button
                    onClick={handleLogout}
                    sx={{
                        borderRadius: "8px",
                        backgroundColor: "#C1272D",
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: "#a01f25",
                            transform: "scale(1.02)",
                            transition: "all 0.2s ease",
                        },
                    }}
                >
                    <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
                        <Logout />
                    </ListItemIcon>
                    <ListItemText
                        primary="Logout"
                        primaryTypographyProps={{
                            fontWeight: 600,
                            fontSize: "0.9rem",
                        }}
                    />
                </ListItem>
            </Box>
        </>
    );

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={drawerOpen}
            onClose={() => {
                if (onToggle) onToggle(false);
            }}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: 280,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: 280,
                    boxSizing: "border-box",
                    top: 0,
                    height: "100vh",
                    border: "none",
                    boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
}
